import { Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { mdiChevronDown, mdiChevronUp, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import ActionContainer from 'components/ActionContainer/ActionContainer';
import Button from 'components/Button/Button';
import Loading from 'components/Loading/Loading';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FirestopProductItem } from './FirestopProductItem';

const useStyles = makeStyles((theme) => ({
  emptyState: {
    padding: theme.spacing(1, 0),
  },
}));

export const FirestopProductsSection = ({
  loading,
  isLoadingFirestopProducts,
  open,
  setOpen,
  firestopProducts,
  onAddFirestopProduct,
  onChangeFirestopProduct,
  onDeleteFirestopProduct,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const [openFirestopProductsArray, setOpenFirestopProductsArray] = useState(
    []
  );

  const activeFirestopProducts = useMemo(
    () =>
      firestopProducts.filter((firestopProduct) => !firestopProduct.deletedAt),
    [firestopProducts]
  );

  useEffect(() => {
    setOpenFirestopProductsArray((currentOpenFirestopProductsArray) =>
      currentOpenFirestopProductsArray.filter((id) =>
        activeFirestopProducts.some(
          (firestopProduct) => firestopProduct.id === id
        )
      )
    );
  }, [activeFirestopProducts]);

  const handleAddFirestopProduct = () => {
    const createdFirestopProductId = onAddFirestopProduct();

    if (!createdFirestopProductId) {
      return;
    }

    setOpenFirestopProductsArray((currentOpenFirestopProductsArray) => [
      ...currentOpenFirestopProductsArray,
      createdFirestopProductId,
    ]);
  };

  return (
    <ActionContainer
      title={t('project_dialog.section_firestop_products')}
      closable
      open={open}
      setOpen={setOpen}
      color="darker"
    >
      {isLoadingFirestopProducts ? (
        <Grid item xs={12}>
          <Loading tiny />
        </Grid>
      ) : activeFirestopProducts.length > 0 ? (
        <>
          <Grid container item spacing={1} justifyContent="flex-end">
            <Grid item xs="auto">
              {openFirestopProductsArray.length ? (
                <Button
                  color="primary"
                  outline
                  disabled={loading}
                  onClick={() => setOpenFirestopProductsArray([])}
                >
                  {t('project_dialog.button_close_all')}
                  <Icon path={mdiChevronUp} size={1} />
                </Button>
              ) : (
                <Button
                  color="primary"
                  outline
                  disabled={loading}
                  onClick={() =>
                    setOpenFirestopProductsArray(
                      activeFirestopProducts.map(
                        (firestopProduct) => firestopProduct.id
                      )
                    )
                  }
                >
                  {t('project_dialog.button_open_all')}
                  <Icon path={mdiChevronDown} size={1} />
                </Button>
              )}
            </Grid>
          </Grid>
          {activeFirestopProducts.map((firestopProduct) => (
            <Grid container item key={firestopProduct.id}>
              <FirestopProductItem
                firestopProduct={firestopProduct}
                loading={loading || isLoadingFirestopProducts}
                openFirestopProductsArray={openFirestopProductsArray}
                setOpenFirestopProductsArray={setOpenFirestopProductsArray}
                onChangeFirestopProduct={onChangeFirestopProduct}
                onDeleteFirestopProduct={onDeleteFirestopProduct}
              />
            </Grid>
          ))}
        </>
      ) : (
        <Grid item xs={12}>
          <Typography className={classes.emptyState}>
            {t('common.no_options')}
          </Typography>
        </Grid>
      )}
      <Grid item xs={12} style={{ marginTop: 8 }}>
        <Button
          color="primary"
          fullWidth
          disabled={loading || isLoadingFirestopProducts}
          onClick={handleAddFirestopProduct}
        >
          <Icon path={mdiPlus} size={1} />
          {t('project_dialog.button_add_new')}
        </Button>
      </Grid>
    </ActionContainer>
  );
};

FirestopProductsSection.propTypes = {
  loading: PropTypes.bool,
  isLoadingFirestopProducts: PropTypes.bool,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  firestopProducts: PropTypes.array,
  onAddFirestopProduct: PropTypes.func,
  onChangeFirestopProduct: PropTypes.func,
  onDeleteFirestopProduct: PropTypes.func,
};

FirestopProductsSection.defaultProps = {
  loading: false,
  isLoadingFirestopProducts: false,
  open: false,
  setOpen: () => {},
  firestopProducts: [],
  onAddFirestopProduct: () => {},
  onChangeFirestopProduct: () => {},
  onDeleteFirestopProduct: () => {},
};
